import type { InvoiceItem, InvoiceItemFulfillment } from "@/lib/api/types";

/** Which moment a status group is dated by: when its plates ENTERED that state. */
const STAMP: Partial<Record<InvoiceItemFulfillment, (item: InvoiceItem) => string | null | undefined>> = {
  DRAFT: (item) => item.createdAt,
  QUEUED: (item) => item.firedAt,
  PREPARING: (item) => item.preparingAt,
  READY: (item) => item.readyAt,
  SERVED: (item) => item.servedAt,
  VOIDED: (item) => item.voidedAt,
};

/**
 * THE TIME ABOVE A STATUS GROUP (founder, 2026-10-06): the earliest of its
 * plates' entry into that state — the oldest one waiting is what the floor
 * needs to see — or null when none carries the stamp.
 */
export function sectionStartedAt(
  items: readonly InvoiceItem[],
  state: InvoiceItemFulfillment,
): string | null {
  const read = STAMP[state];
  if (!read) return null;
  let earliest: string | null = null;
  for (const item of items) {
    if (item.fulfillmentState !== state) continue;
    const at = read(item);
    if (!at || Number.isNaN(new Date(at).getTime())) continue;
    if (earliest === null || new Date(at) < new Date(earliest)) earliest = at;
  }
  return earliest;
}

/**
 * "23:43" today, "5 Oct 23:43" on any other day — in the store's zone, with
 * Latin digits in Arabic like the rest of the till's clocks.
 */
export function formatSectionStamp(
  instant: string,
  timezone: string | null | undefined,
  locale: "ar" | "en",
  now: Date = new Date(),
): string {
  const at = new Date(instant);
  if (Number.isNaN(at.getTime())) return "";
  const tag = locale === "ar" ? "ar-EG-u-nu-latn" : "en-GB";
  const zone = (() => {
    if (!timezone) return undefined;
    try {
      new Intl.DateTimeFormat(tag, { timeZone: timezone });
      return timezone;
    } catch {
      return undefined;
    }
  })();
  const day = new Intl.DateTimeFormat("en-CA", { timeZone: zone, year: "numeric", month: "2-digit", day: "2-digit" });
  const sameDay = day.format(at) === day.format(now);
  return new Intl.DateTimeFormat(tag, {
    timeZone: zone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    ...(sameDay ? {} : { day: "numeric", month: "short" }),
  }).format(at);
}
